import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Card } from './ui/Card';
import { Alert } from './ui/Alert';
import { Label } from './ui/Label';
import { apiFetch } from '../lib/api';
import { AUTO_LABEL_NAMES } from '../lib/automationLabels';
import { formatNumber, fromLocalInputs, toLocalInputs } from '../lib/format';
import { useDebouncedValue } from '../lib/useDebouncedValue';
import {
  ApiError,
  type AudienceEstimate,
  type AutoLabel,
  type CampaignAudience,
  type LabelsOverviewResponse,
  type MailingList,
  type Segment,
  type Sender,
} from '../types/api';

export const EMPTY_AUDIENCE: CampaignAudience = {
  listIds: [],
  includeLabels: [],
  excludeLabels: [],
  includeSegmentIds: [],
  excludeSegmentIds: [],
  excludeOrderedSince: null,
};

export const SELECT_CLASS =
  'h-[42px] w-full rounded-[9px] border border-zinc-300 bg-card px-3 text-sm text-text disabled:opacity-60';

function toggle(values: string[], value: string, on: boolean): string[] {
  return on ? [...new Set([...values, value])] : values.filter((v) => v !== value);
}

/** Active senders, for the campaign's "Sender" select. */
export function useSenders() {
  return useQuery({
    queryKey: ['senders'],
    queryFn: () => apiFetch<{ items: Sender[] }>('/senders'),
  });
}

export function SenderSelect({
  id,
  value,
  onChange,
  disabled,
}: {
  id: string;
  value: string;
  onChange: (senderId: string) => void;
  disabled?: boolean | undefined;
}) {
  const { data, isLoading, isError } = useSenders();
  const senders = data?.items ?? [];
  // A campaign whose sender was archived afterwards still shows it, marked.
  const current = data && value && !senders.some((s) => s.id === value) ? value : null;

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>Sender</Label>
      <select
        id={id}
        className={SELECT_CLASS}
        value={value}
        required
        disabled={disabled || isLoading}
        aria-describedby={isError || senders.length === 0 ? `${id}-hint` : undefined}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="" disabled>
          {isLoading ? 'Loading senders…' : 'Select sender'}
        </option>
        {current ? <option value={current}>Archived sender</option> : null}
        {senders.map((s) => (
          <option key={s.id} value={s.id}>
            {s.fromName} &lt;{s.fromEmail}&gt;
          </option>
        ))}
      </select>
      {isError ? (
        <p id={`${id}-hint`} role="alert" className="m-0 text-xs text-danger-text">
          The senders could not be loaded.
        </p>
      ) : !isLoading && senders.length === 0 ? (
        <p id={`${id}-hint`} className="m-0 text-xs text-muted">
          There are no senders yet. An administrator can add them under{' '}
          <Link to="/indstillinger/afsendere">Settings → Senders</Link>.
        </p>
      ) : null}
    </div>
  );
}

/** Exported for reuse by the segment editor (`routes/SegmenterPage.tsx`), which needs the same include/exclude-tags checkbox UI. */
export function CheckboxGroup({
  legend,
  options,
  selected,
  disabled,
  onChange,
  empty,
  variant = 'plain',
}: {
  legend: string;
  options: { value: string; label: string; hint?: string }[];
  selected: string[];
  disabled?: boolean | undefined;
  onChange: (next: string[]) => void;
  empty?: string | undefined;
  /** `rows` = bordered option rows, used inside the campaign editor (styles/preflight.css). */
  variant?: 'plain' | 'rows' | undefined;
}) {
  const emptyNote =
    options.length === 0 && empty ? <p className="m-0 text-xs text-muted">{empty}</p> : null;
  if (variant === 'rows') {
    return (
      <fieldset className="m-0 p-0 border-0 flex flex-col gap-1.5 min-w-0" disabled={disabled}>
        <legend className="text-sm font-medium mb-1">{legend}</legend>
        {emptyNote}
        {options.length > 0 ? (
          <div className="pf-opts">
            {options.map((o) => (
              <label key={o.value} className="pf-opt">
                <input
                  type="checkbox"
                  checked={selected.includes(o.value)}
                  onChange={(e) => onChange(toggle(selected, o.value, e.target.checked))}
                />
                <span className="pf-opt-nm">{o.label}</span>
                {o.hint ? <span className="pf-opt-ct">{o.hint}</span> : null}
              </label>
            ))}
          </div>
        ) : null}
      </fieldset>
    );
  }
  return (
    <fieldset className="m-0 p-0 border-0 flex flex-col gap-1.5" disabled={disabled}>
      <legend className="text-sm font-medium mb-1">{legend}</legend>
      {emptyNote}
      {options.map((o) => (
        <label key={o.value} className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={selected.includes(o.value)}
            onChange={(e) => onChange(toggle(selected, o.value, e.target.checked))}
          />
          <span>{o.label}</span>
          {o.hint ? <span className="text-xs text-muted">{o.hint}</span> : null}
        </label>
      ))}
    </fieldset>
  );
}

/**
 * "Skip contacts who ordered since": a date and time in the browser's time
 * zone, kept as its own text until both parts are valid, so a half-typed
 * date never clears the stored cutoff.
 */
export function OrderedSinceField({
  value,
  onChange,
  disabled,
}: {
  value: number | null;
  onChange: (next: number | null) => void;
  disabled?: boolean | undefined;
}) {
  const [date, setDate] = useState(() => (value !== null ? toLocalInputs(value).date : ''));
  const [time, setTime] = useState(() => (value !== null ? toLocalInputs(value).time : '00:00'));
  // The editor loads the campaign after the first render.
  useEffect(() => {
    if (value === null) return;
    const local = toLocalInputs(value);
    setDate(local.date);
    setTime(local.time);
  }, [value]);

  const on = value !== null;
  const change = (nextDate: string, nextTime: string) => {
    setDate(nextDate);
    setTime(nextTime);
    const at = fromLocalInputs(nextDate, nextTime);
    if (at !== null) onChange(at);
  };

  return (
    <fieldset className="border-0 p-0 m-0 flex flex-col gap-2 text-sm">
      <legend className="sr-only">Recent buyers</legend>
      <label className="flex gap-2 items-center cursor-pointer">
        <input
          type="checkbox"
          checked={on}
          disabled={disabled}
          onChange={(e) => {
            if (!e.target.checked) {
              onChange(null);
              return;
            }
            const start = date !== '' ? date : toLocalInputs(Date.now()).date;
            const at = fromLocalInputs(start, time);
            setDate(start);
            if (at !== null) onChange(at);
          }}
        />
        <span className="font-medium">Skip contacts who ordered since</span>
      </label>
      {on ? (
        <div className="flex flex-wrap gap-2 items-center pl-6">
          <Label htmlFor="audience-ordered-since-date" className="sr-only">
            Date
          </Label>
          <input
            id="audience-ordered-since-date"
            type="date"
            className={`${SELECT_CLASS} w-auto`}
            value={date}
            disabled={disabled}
            onChange={(e) => change(e.target.value, time)}
          />
          <Label htmlFor="audience-ordered-since-time" className="sr-only">
            Time
          </Label>
          <input
            id="audience-ordered-since-time"
            type="time"
            className={`${SELECT_CLASS} w-auto`}
            value={time}
            disabled={disabled}
            onChange={(e) => change(date, e.target.value)}
          />
        </div>
      ) : null}
      <p className="m-0 pl-6 text-xs text-muted">
        Everyone with an order (processing, on hold or completed) placed at or after this time is
        left out. It is checked again right before each email goes out, so an order placed after
        approval counts too. Use it when several emails go out during a sale.
      </p>
    </fieldset>
  );
}

export function audienceErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.body.message) return error.body.message;
  return 'The audience could not be calculated. Try again shortly.';
}

/**
 * Lists, labels and segments for a campaign plus the live estimate
 * (`POST /api/campaigns/audience-estimate`). Lists are a union; labels
 * and segments narrow it.
 *
 * `embedded` drops the card and heading and shows the lists as option
 * rows, for the "To" step of the campaign editor.
 */
export function AudiencePicker({
  value,
  onChange,
  disabled,
  embedded = false,
}: {
  value: CampaignAudience;
  onChange: (next: CampaignAudience) => void;
  disabled?: boolean | undefined;
  embedded?: boolean | undefined;
}) {
  const lists = useQuery({
    queryKey: ['lists'],
    queryFn: () => apiFetch<{ items: MailingList[] }>('/lists'),
  });
  const segments = useQuery({
    queryKey: ['segments'],
    queryFn: () => apiFetch<{ items: Segment[] }>('/segments'),
  });
  // Every label with at least one contact — automatic (the 7 fixed ones)
  // and manual (assigned by hand, or from a Mailchimp TAGS import, P7).
  // `GET /api/labels` already aggregates both (packages/core/src/labels/
  // compute.ts#getLabelCounts), so the audience picker reuses it instead
  // of a new endpoint.
  const labels = useQuery({
    queryKey: ['labels'],
    queryFn: () => apiFetch<LabelsOverviewResponse>('/labels'),
  });
  const debounced = useDebouncedValue(value, 300);
  const estimate = useQuery({
    queryKey: ['audience-estimate', debounced],
    queryFn: () =>
      apiFetch<AudienceEstimate>('/campaigns/audience-estimate', {
        method: 'POST',
        body: debounced,
      }),
  });

  const listOptions = (lists.data?.items ?? []).map((l) => ({
    value: l.id,
    label: l.name,
    hint: `${formatNumber(l.confirmedCount)} confirmed`,
  }));
  // A list archived after it was picked stays visible so it can be removed.
  for (const id of value.listIds) {
    if (!listOptions.some((o) => o.value === id)) {
      listOptions.push({ value: id, label: 'Archived list', hint: '' });
    }
  }
  // All labels the tenant actually has (auto + manual), not just the 7
  // hardcoded automatic ones — a manual label (assigned by hand, or from a
  // Mailchimp TAGS import) must be selectable for include/exclude too.
  // Known auto labels get their friendly name; anything else (manual)
  // shows its raw slug, which is already what admins see and type
  // elsewhere (e.g. on a contact's page).
  const labelOptions = (labels.data?.counts ?? [])
    .map((c) => ({
      value: c.label,
      label: c.label in AUTO_LABEL_NAMES ? AUTO_LABEL_NAMES[c.label as AutoLabel] : c.label,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
  // A label already selected but no longer returned (e.g. a manual label
  // that lost its last contact) stays visible so it can still be removed.
  for (const l of [...value.includeLabels, ...value.excludeLabels]) {
    if (!labelOptions.some((o) => o.value === l)) {
      labelOptions.push({
        value: l,
        label: l in AUTO_LABEL_NAMES ? AUTO_LABEL_NAMES[l as AutoLabel] : l,
      });
    }
  }
  const segmentOptions = (segments.data?.items ?? []).map((s) => ({ value: s.id, label: s.name }));
  const set = (patch: Partial<CampaignAudience>) => onChange({ ...value, ...patch });

  const variant = embedded ? 'rows' : 'plain';
  const body = (
    <>
      {lists.isError ? <Alert>The lists could not be loaded.</Alert> : null}
      {labels.isError ? <Alert>The tags could not be loaded.</Alert> : null}
      <CheckboxGroup
        legend="Lists"
        options={listOptions}
        selected={value.listIds}
        disabled={disabled}
        onChange={(listIds) => set({ listIds })}
        empty={lists.isLoading ? 'Loading lists…' : 'There are no lists yet.'}
        variant={variant}
      />
      <details className="text-sm">
        <summary className="cursor-pointer font-medium">Narrow down with tags and segments</summary>
        <div
          className={
            embedded ? 'grid grid-cols-1 gap-4 mt-3 sm:grid-cols-2' : 'grid grid-cols-2 gap-4 mt-3'
          }
        >
          <CheckboxGroup
            legend="Only contacts with all of these tags"
            options={labelOptions}
            selected={value.includeLabels}
            disabled={disabled}
            onChange={(includeLabels) => set({ includeLabels })}
            empty={labels.isLoading ? 'Loading tags…' : 'There are no tags yet.'}
          />
          <CheckboxGroup
            legend="Exclude contacts with any of these tags"
            options={labelOptions}
            selected={value.excludeLabels}
            disabled={disabled}
            onChange={(excludeLabels) => set({ excludeLabels })}
            empty={labels.isLoading ? 'Loading tags…' : 'There are no tags yet.'}
          />
          <CheckboxGroup
            legend="Only contacts in all of these segments"
            options={segmentOptions}
            selected={value.includeSegmentIds}
            disabled={disabled}
            onChange={(includeSegmentIds) => set({ includeSegmentIds })}
            empty="No segments yet."
          />
          <CheckboxGroup
            legend="Exclude contacts in any of these segments"
            options={segmentOptions}
            selected={value.excludeSegmentIds}
            disabled={disabled}
            onChange={(excludeSegmentIds) => set({ excludeSegmentIds })}
            empty="No segments yet."
          />
        </div>
        <p className="m-0 mt-2 text-xs text-muted">
          Contacts who have objected to profiling are excluded when narrowing down with tags or
          segments.
        </p>
      </details>
      <OrderedSinceField
        value={value.excludeOrderedSince}
        disabled={disabled}
        onChange={(excludeOrderedSince) => set({ excludeOrderedSince })}
      />
      <div aria-live="polite" className="text-sm">
        {estimate.isError ? (
          <span className="text-danger-text">{audienceErrorMessage(estimate.error)}</span>
        ) : estimate.data ? (
          <span>
            Estimated <strong>{formatNumber(estimate.data.estimatedRecipients)}</strong> recipients
            <span className="text-muted">
              {' '}
              ({formatNumber(estimate.data.listMembers)} on the lists before unsubscribes, consent
              and narrowing)
            </span>
            {estimate.data.heldByFrequencyCap > 0 ? (
              <span className="block text-muted">
                {formatNumber(estimate.data.heldByFrequencyCap)}{' '}
                {estimate.data.heldByFrequencyCap === 1 ? 'contact has' : 'contacts have'} already
                had 2 emails in the last 7 days and{' '}
                {estimate.data.heldByFrequencyCap === 1 ? 'is' : 'are'} held back by the frequency
                limit.
              </span>
            ) : null}
          </span>
        ) : (
          <span className="text-muted">Calculating recipients…</span>
        )}
      </div>
    </>
  );
  if (embedded) return <div className="flex flex-col gap-4">{body}</div>;
  return (
    <Card className="p-4 flex flex-col gap-4">
      <h2 className="text-base font-semibold m-0">Audience</h2>
      {body}
    </Card>
  );
}
